<script setup lang="ts">
  import { ref, withDefaults, defineProps, onMounted, watch, computed, reactive } from 'vue'
  import Selecto from 'vue3-selecto'
  import { GroupFields, GroupFieldsOptions, MovableComponentEvents, SelectoComponentEvents } from '../../types'
  import Moveable, { BoundType } from 'vue3-moveable'
  import { useMovable } from '../../composition/useMovable'
  import { CommonGroupTooltip } from '../Tooltip'
  import { fetchExistingGroup, getGroupInfoWithElements, getRequiredValidation } from '../../utils/group'
  import { PlusSquareIcon } from '@gohighlevel/ghl-icons/24/outline'
  import { UITooltip } from '@gohighlevel/ghl-ui'
  import { ELEMENTS_LOOKUP } from '../../const/elements'
  import { OLD_PAGE_MARGIN } from '../../const'
  import { calculatePercentage } from '../../utils/percentage'

  const emit = defineEmits([
    SelectoComponentEvents.ON_SELECTO_SELECT_END,
    SelectoComponentEvents.ON_SELECTO_GROUP,
    SelectoComponentEvents.ON_SELECTO_UNGROUP,
    SelectoComponentEvents.ON_SELECTO_CLONE,
    SelectoComponentEvents.ON_SELECTO_DELETE,
    SelectoComponentEvents.ON_SELECTO_CLICK_GROUP,
    SelectoComponentEvents.ON_CLONE_AND_ADD_TO_GROUP,
    MovableComponentEvents.ON_DRAG_END,
    MovableComponentEvents.ON_DRAG,
  ])

  export interface ElementRef<T> {
    current?: T | undefined | null
    value?: T | undefined | null
  }
  const moveableSelectionRef = ref(null)
  const groupTooltipAbleRef = ref(null)
  const selectoRef = ref(null)
  const targets = ref([])
  const displayToolTip = ref(false)
  const displayAddToGroupToolTip = ref(false)
  const cloneAndAddElementToGroup = ref(null)
  const groupRequiredAbleRef = ref(null)
  const activeGroupInfo = ref()
  const isDragging = ref(false)

  const props = withDefaults(
    defineProps<{
      pageWrapperRef?: any
      scrollOptions?: any
      snapThreshold?: number
      storeMap?: any
      container?: HTMLElement | null
      rootContainer?: HTMLElement | null
      dragContainer?: Element | Window | Element[] | string | null | undefined
      className?: string
      selectableTargets?: Array<Element | string | (() => Element | Element[]) | ElementRef<Element>>
      selectFromInside?: boolean
      selectByClick?: boolean
      preventDragFromInside?: boolean
      clickBySelectEnd?: boolean
      continueSelect?: boolean
      continueSelectWithoutDeselect?: boolean
      toggleContinueSelect?: string[][] | string[] | string | null
      toggleContinueSelectWithoutDeselect?: string[][] | string[] | string | null
      keyContainer?: Document | HTMLElement | Window | null
      hitRate?: number | string | ((element: Element) => number | string)
      ratio?: number
      dragStart?: any
      select?: any
      selectEnd?: any
      colorOverride?: string
      inactiveColorOverride?: string
      compositionMap: {
        type: object
        required: true
      }
      activeGroup: any
    }>(),
    {
      snapThreshold: 5,
      colorOverride: '#4af',
      inactiveColorOverride: '#4af',
    }
  )

  const { updateStyles, updateGroupStyle } = props.compositionMap.useElementOption()
  const { movableRefMap, setSelectionMovableRef, tooltipRef } = useMovable()
  const builderStore = props.storeMap.useBuilderStore()
  const isEditable = computed(() => builderStore.isDocumentEditable)

  const selectedElements = computed(() => {
    const selectedElementsMap = targets.value?.map(ev => {
      const elementId = ev?.getAttribute('data-element-id')
      const pageId = ev?.getAttribute('data-page-id')
      const pageIndex = ev?.getAttribute('data-page-index')
      return { elementId, pageId, pageIndex }
    })
    return selectedElementsMap
  })

  const groupSelectedExist = computed(() => {
    const currentGroups = builderStore.groups
    const groupExist = fetchExistingGroup({
      currentGroups,
      group: selectedElements.value,
    })
    return groupExist
  })

  const groupExist = computed(() => {
    return groupSelectedExist.value && groupSelectedExist.value.id ? true : false
  })

  const bounds = computed(
    () =>
      ({
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        position: 'css',
      }) as BoundType
  )

  const directions = computed(() => ({
    top: true,
    left: true,
    bottom: true,
    right: true,
  }))

  // eslint-disable-next-line vue/no-dupe-keys
  const elementSnapDirections = computed(() => {
    return {
      top: true,
      left: true,
      bottom: true,
      right: true,
      center: true,
      middle: true,
    }
  })

  const snapContainer = computed(() => {
    const pageIndexes: string[] = []
    // Track which section (headers / footers / content) every selected target lives in. Each
    // PageSection root carries `data-section-type="headers" | "footers"`; `.page-content` has no
    // such attribute so we bucket those under `'content'`. If every selected element belongs to
    // the same page + same section, we can return the correctly-scoped snap root. Otherwise we
    // fall back to `.pages-wrapper`.
    const sectionTypes: string[] = []
    for (const element of targets.value as any[]) {
      const pageIndex: string = element?.dataset?.pageIndex || ''
      if (pageIndex && !pageIndexes.includes(pageIndex)) pageIndexes.push(pageIndex)
      const sectionRoot = element?.closest?.('[data-section-type]') as HTMLElement | null
      const sectionType = sectionRoot?.getAttribute?.('data-section-type') || 'content'
      if (!sectionTypes.includes(sectionType)) sectionTypes.push(sectionType)
    }

    if (pageIndexes.length === 1 && sectionTypes.length === 1) {
      const pageIndex = pageIndexes[0]
      const sectionType = sectionTypes[0]
      // Header / footer elements MUST snap / bound against their own PageSection root — not the
      // page's `.page-content`. Using `.page-content` (`.snapContainer-${pageIndex}`) when the
      // group lives in a header made `getNearestSnap` fall back to page-content, and drag-end
      // position math computed `snappedTop` against page-content's rect. Since the header sits
      // above page-content, the group visibly jumped to (or past) page-content's top as soon as
      // the drag started. `.snapContainer-${pageIndex}-section-${sectionType}` is the
      // `floatingSnapContainerClass` that PageSection.vue applies to the header / footer root.
      if (sectionType === 'headers' || sectionType === 'footers') {
        return `.snapContainer-${pageIndex}-section-${sectionType}`
      }
      return `.snapContainer-${pageIndex}`
    }
    return `.pages-wrapper`
  })

  const isDragEnabled = computed(() => {
    // get all the page id of the elements.
    const selectedCheckBox = targets.value.reduce((acc, element: any) => {
      // Check if the id already exists in the new array
      const pageId: string = element?.dataset?.pageId || ''
      if (pageId && !acc.includes(pageId)) {
        // If it doesn't exist, add it to the new array
        acc.push(pageId)
      }
      return acc
    }, [])

    // check all the elements Id is belongs to one page.
    if (selectedCheckBox && selectedCheckBox.length === 1) {
      return true
    }
    return false
  })

  /*
   * This is to set the tool tip position for the selected group. [Top Tooltip]
   * It will set the absolute position based on the group movable box.
   */
  const setGroupTooltipPosition = ({ top, left, width }: { top: number; left: number; width: number }) => {
    if (groupTooltipAbleRef.value) {
      groupTooltipAbleRef.value.style.top = `${top - 15}px`
      groupTooltipAbleRef.value.style.right = `calc(100% - ${left + width}px)`
    }
  }

  /*
   * This tooltip show in the bottom of the group movable box.[Bottom Tooltip]
   * The movement of + icon in the group movable box changing based on this function.
   */
  const setAddToGroupTooltipPosition = ({ top, left, height, width }: { top: number; height: number; left: number; width: number }) => {
    if (cloneAndAddElementToGroup.value) {
      cloneAndAddElementToGroup.value.style.top = `${top + height + 2}px`
      cloneAndAddElementToGroup.value.style.right = `calc(100% - ${left + width / 2 + 15}px)`
    }
  }

  const setRequiredPosition = ({ top, left, width }: { top: number; left: number; width: number }) => {
    if (groupRequiredAbleRef.value) {
      groupRequiredAbleRef.value.style.top = `${top - 20}px`
      groupRequiredAbleRef.value.style.right = `calc(100% - ${left + width + 6}px)`
    }
  }

  const onDragGroup = e => {
    isDragging.value = true
    e.events.forEach(ev => {
      const elementId = ev.target.getAttribute('data-element-id')
      movableRefMap.value[elementId]?.updateRect()
      // CRITICAL: during the drag, move each element VISUALLY via Moveable's
      // own `transform` (mirrors `MovableComponentWrapper.onDrag`). Do NOT
      // write `ev.top` / `ev.left` back into the data model (`position.top`
      // / `position.left`) here — Vue's reactivity then re-paints the
      // element at the new CSS top/left WHILE Moveable continues applying
      // its accumulated transform on each frame, causing the element to
      // visually move at 2× the cursor speed and drift outside its
      // containing block almost immediately. The effect is most visible
      // inside header / footer sections (small containing block, drift
      // exits the box on the first move) but the same bug applies to body
      // pages too — it just takes longer to escape `.page`'s bounds.
      //
      // Final container-relative `position.top` / `position.left` is
      // computed and persisted in `onDragGroupEnd` → `onDragEnd`, which
      // uses `getNearestSnap` + viewport-to-container math identical to the
      // single-drag path.
      ev.target.style.transform = ev.transform
    })
    if (groupSelectedExist.value && groupSelectedExist.value.id) {
      updateGroupStyle(
        'position',
        {
          top: e.top,
          left: e.left,
        },
        groupSelectedExist.value.id
      )
    }
    moveableSelectionRef?.value?.updateRect()
    syncGroupTooltip()
  }

  const syncGroupTooltip = () => {
    if (targets.value.length > 1 && moveableSelectionRef.value) {
      moveableSelectionRef.value.updateRect()
      displayToolTip.value = true
      displayAddToGroupToolTip.value = true
      const { top, left, height, width } = moveableSelectionRef.value.getRect()
      if (top && left && height && width) {
        setGroupTooltipPosition({ top, left, width })
        setAddToGroupTooltipPosition({ top, left, height, width })
        setRequiredPosition({ top, left, width })
      }
    }
    if (targets.value.length <= 1) {
      displayToolTip.value = false
      displayAddToGroupToolTip.value = false
    }
  }

  const onDragStart = e => {
    if (e.isDouble) {
      targets.value = []
      e.stop()
    }
    moveableSelectionRef?.value?.updateRect()
    const moveable = moveableSelectionRef.value
    const target = e.inputEvent.target
    const movableRefMapList = Object.values(movableRefMap?.value)
    const flatted = targets.value.flat(3)
    const isMovable = movableRefMapList.some(moveRef => {
      return moveRef?.isMoveableElement(target)
    })

    if (targets.value && targets.value.length > 0) {
      const isMovableSelected = moveableSelectionRef?.value?.isMoveableElement(target)
      if (isMovableSelected) {
        // targets.value?.forEach(ev => {
        //   const elementId = ev.getAttribute('data-element-id')
        //   const elementClicked = movableRefMap.value[elementId]?.isInside(
        //     e.clientX,
        //     e.clientY
        //   )
        //   // the movable selection is there. and we need to click the checkbox now.
        //   if (elementClicked) {
        //     console.log('elementId clicked', elementId)
        //   }
        // })
        e.stop()
      }
    }

    const isTextField =
      target.classList.contains('n-input__textarea-el') &&
      target?.parentElement?.parentElement?.parentElement?.parentElement?.classList?.contains('el-wrapper')
    const isFillableElements = target?.offsetParent?.classList?.contains('el-wrapper')

    const groupToolTipClick = groupTooltipAbleRef.value && groupTooltipAbleRef.value?.contains(target)

    const groupElementCloneClick = cloneAndAddElementToGroup.value && cloneAndAddElementToGroup.value?.contains(target)

    const isStaticElementDragging =
      target?.classList?.contains('remove-selecto-selection') || target?.parentElement?.classList?.contains('remove-selecto-selection')

    if (
      isTextField ||
      isFillableElements ||
      isMovable ||
      target?.classList?.contains('el-wrapper') ||
      isStaticElementDragging ||
      target?.classList?.contains('n-checkbox-box__border') ||
      groupToolTipClick ||
      groupElementCloneClick ||
      moveable?.isMoveableElement(target) ||
      flatted.some(t => t === target || t?.contains(target))
    ) {
      e.preventDrag()
      if (groupToolTipClick || groupElementCloneClick) {
        e.stop()
      }
    }
    syncGroupTooltip()
  }

  const getNearestSnap = (targetElement: HTMLElement) => {
    const divs = document.querySelectorAll(`${snapContainer.value} .boundBox`)

    let nearestDiv: HTMLElement | null = null

    const targetRect = targetElement.getBoundingClientRect()
    const { top: targetTop, left: targetLeft, bottom: targetBottom, right: targetRight } = targetRect

    const findNearestDiv = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect()

      // Check if the top-left corner of the target element is within this element
      const isInside = targetLeft >= rect.left && targetTop + 1 >= rect.top && rect.bottom >= targetBottom + 1 && rect.right >= targetRight

      if (isInside) {
        nearestDiv = element
        element.querySelectorAll(`${snapContainer.value}`).forEach(child => findNearestDiv(child as HTMLElement))
      }
    }

    divs.forEach(div => {
      if (div && div.style) {
        div.style.outline = 'none'
      }
      findNearestDiv(div as HTMLElement)
    })

    if (!nearestDiv) {
      nearestDiv = document.querySelector(`${snapContainer.value}`)
    }

    const id = (nearestDiv as unknown as HTMLElement)?.getAttribute('data-id')
    const type = (nearestDiv as unknown as HTMLElement)?.getAttribute('data-type')
    const pageId = (nearestDiv as unknown as HTMLElement)?.getAttribute('data-page-id')
    // Header / footer section roots set `data-section-type="headers" | "footers"`. We need this
    // in the drag-end payload so the consumer (PagesLoop → builderStore.moveFloatingElement)
    // can route the position update through `updateSectionElementStyle`, which propagates the
    // change to every page that mirrors this header/footer. Without it, group-drag end falls
    // through to `updateElementStyle` (local-only) and the moved element drifts out of sync
    // across pages — single-drag works because MovableComponentWrapper.getNearestSnap already
    // returns sectionType, and FloatingCheckBoxElement forwards it into moveFloatingElement.
    const sectionType = (nearestDiv as unknown as HTMLElement)?.getAttribute('data-section-type')
    /**
   * @description This code is only for debugging purpose. DO NO REMOVE
   * Highlight the page element when the target element is a page
   *

  if (nearestDiv && (nearestDiv as HTMLElement).style) {
    nearestDiv.style.outlineOffset = '-1px'
    nearestDiv.style.borderRadius = '3px'
    nearestDiv.style.outline = '1px dashed #fad1d1'
  }

  if (
    type === ELEMENTS_LOOKUP.PAGE &&
    (nearestDiv as unknown as HTMLElement).style
  ) {
    nearestDiv.style.outline = '1px dashed #e4fad1'
    nearestDiv.style.outlineOffset = '-1px'
    nearestDiv.style.borderRadius = '3px'
  }
  **/
    return {
      id,
      type,
      pageId,
      sectionType,
      div: nearestDiv as unknown as HTMLDivElement,
    }
  }
  const offsetX = reactive<Record<string, number>>({})
  const offsetY = reactive<Record<string, number>>({})
  const initialParentContainerType = reactive<Record<string, string | null>>({})

  const checkIfPreferBottom = (target: HTMLElement, topPosition: number) => {
    const rect = target?.getBoundingClientRect()
    if (!rect) {
      return false
    }
    const percentage = calculatePercentage(rect?.height, 60)
    return topPosition >= percentage
  }

  const checkIfPreferRight = (target: HTMLElement, leftPosition: number) => {
    const rect = target?.getBoundingClientRect()
    if (!rect) {
      return false
    }
    const percentage = calculatePercentage(rect?.width, 50)
    return leftPosition >= percentage
  }

  const onDragEnd = event => {
    if (event.lastEvent) {
      const elementId = event?.target?.getAttribute('data-element-id')
      const { top, left, height, width, bottom, right } = event.lastEvent
      const parentInfo = getNearestSnap(event?.target as HTMLElement)
      const rect = parentInfo.div?.getBoundingClientRect()
      let snappedTop = 0
      let snappedRight = 0
      let snappedLeft = 0
      let snappedBottom = 0
      if (parentInfo.type !== ELEMENTS_LOOKUP.PAGE || initialParentContainerType[elementId] !== ELEMENTS_LOOKUP.PAGE) {
        snappedTop = event?.inputEvent?.clientY - (rect?.top || 0) + (parentInfo.div?.scrollTop || 0) - offsetY[elementId]
        snappedLeft = event?.inputEvent?.clientX - (rect?.left || 0) + (parentInfo.div?.scrollLeft || 0) - offsetX[elementId]
        snappedBottom = rect?.height - (snappedTop + height)
        snappedRight = rect?.width - (snappedLeft + width)
      } else {
        snappedTop = top
        snappedLeft = left
        snappedBottom = bottom
        snappedRight = right
      }

      if (initialParentContainerType[elementId] !== ELEMENTS_LOOKUP.PAGE && parentInfo.type === ELEMENTS_LOOKUP.PAGE) {
        snappedBottom = snappedBottom + OLD_PAGE_MARGIN
        snappedLeft = snappedLeft + OLD_PAGE_MARGIN
        snappedRight = snappedRight + OLD_PAGE_MARGIN
        snappedTop = snappedTop + OLD_PAGE_MARGIN
      }
      // Bundle styles + parent + section into ON_DRAG_END so the consumer can
      // call builderStore.moveFloatingElement(elementId, payload) with the
      // FULL payload — exactly like single-drag's ON_DRAG_END payload shape
      // (see MovableComponentWrapper.onDragEnd → dragEndPayload). Without
      // this, the group-drag consumer in PagesLoop only got `parentInfo` in
      // ON_DRAG_END (no styles, no sectionType) and `styles` in a separate
      // ON_DRAG event — and called moveFloatingElement WITHOUT the section
      // routing info, so header/footer position changes were never
      // propagated to the other pages that mirror the section.
      const dragEndPayload = {
        styles: {
          top: snappedTop < -1 ? 0 : snappedTop,
          left: snappedLeft < 0 ? 0 : snappedLeft,
          bottom: snappedBottom,
          right: snappedRight < 0 ? 0 : snappedRight,
          height,
          width,
          preferBottom: checkIfPreferBottom(parentInfo.div, snappedTop),
          preferRight: checkIfPreferRight(parentInfo.div, snappedLeft),
        },
        elementId,
        newParentId: parentInfo.id as string,
        sectionType: parentInfo.sectionType || null,
      }
      emit(MovableComponentEvents.ON_DRAG_END, dragEndPayload, elementId)
      // ON_DRAG kept for backward compat (e.g. consumers that just want the
      // raw new style coords without going through moveFloatingElement). The
      // header/footer-section-aware persistence runs through ON_DRAG_END
      // above.
      emit(
        MovableComponentEvents.ON_DRAG,
        {
          top: snappedTop < -1 ? 0 : snappedTop,
          left: snappedLeft < 0 ? 0 : snappedLeft,
          bottom: snappedBottom,
          right: snappedRight < 0 ? 0 : snappedRight,
          height,
          width,
          preferBottom: checkIfPreferBottom(parentInfo.div, snappedTop),
          preferRight: checkIfPreferRight(parentInfo.div, snappedLeft),
        },
        elementId
      )
      offsetX[elementId] = 0
      offsetY[elementId] = 0

      setTimeout(() => {
        moveableSelectionRef?.value?.updateRect()
      }, 100)
    }
  }

  const onDragGroupStart = e => {
    e.events.forEach(ev => {
      const elementId = ev?.target?.getAttribute('data-element-id')
      if (elementId) {
        movableRefMap.value[elementId]?.updateRect()
        const parentInfo = getNearestSnap(ev.target)
        initialParentContainerType[elementId] = parentInfo.type
        offsetX[elementId] = ev.clientX - ev.target.getBoundingClientRect().left
        offsetY[elementId] = ev.clientY - ev.target.getBoundingClientRect().top
      }
    })
    moveableSelectionRef?.value?.updateRect()
  }

  const onDragGroupEnd = e => {
    isDragging.value = false
    e.events.forEach(ev => {
      const elementId = ev.target.getAttribute('data-element-id')
      movableRefMap.value[elementId]?.updateRect()
      onDragEnd(ev)
      setTimeout(() => syncElementRecipientTooltip(elementId), 15)
    })
    targets.value = selectedElements.value.map(ev => {
      return document.getElementById(ev.elementId)
    })

    moveableSelectionRef?.value?.updateRect()
    setTimeout(() => syncGroupTooltip(), 15)
  }

  const onSelectEnd = e => {
    const moveable = moveableSelectionRef.value
    const selected = e.selected
    const groupInfo = getGroupInfoWithElements(builderStore.groups, selected)
    const { closestGroup, isPartialGroup, group, isConflict, selectedElementsMap } = groupInfo

    if (isConflict) {
      targets.value = []
      e.stop()
    }
    if (closestGroup && isPartialGroup && !group && !isConflict) {
      makeActiveGroupWithSelectedElements(closestGroup, selectedElementsMap)
    }

    if (group && isPartialGroup && !isConflict) {
      activeGroupInfo.value = groupInfo?.group
      makeActiveGroup(group)
    }

    if (!group && !isPartialGroup && !isConflict) {
      targets.value = e.selected
    }

    if (selected && selected.length === 0) {
      targets.value = []
    }

    moveable?.waitToChangeTarget().then(() => {
      moveableSelectionRef?.value?.updateRect()
      syncGroupTooltip()
      const selectedElementMap = targets.value.map(ev => {
        const elementId = ev?.getAttribute('data-element-id')
        const pageId = ev?.getAttribute('data-page-id')
        const pageIndex = ev?.getAttribute('data-page-index')
        return { elementId, pageId, pageIndex }
      })
      emit(SelectoComponentEvents.ON_SELECTO_SELECT_END, {
        targets: targets.value,
        event: e,
        selectedElements: selectedElementMap,
      })
    })
  }

  onMounted(() => {
    syncGroupTooltip()
    setSelectionMovableRef(moveableSelectionRef.value)
  })

  const syncElementRecipientTooltip = (elementId: string) => {
    const rect = movableRefMap.value[elementId]?.getRect()
    if (rect) {
      const { height, top, left } = rect
      if (tooltipRef.value[elementId]) {
        tooltipRef.value[elementId].style.top = `${top + height + 15}px`
        tooltipRef.value[elementId].style.left = `${left + 15}px`
      }
    }
  }

  const onChangeTargets = e => {
    e.targets.forEach(ev => {
      const elementId = ev.getAttribute('data-element-id')
      movableRefMap.value[elementId]?.updateRect()
    })
    setTimeout(() => syncGroupTooltip(), 15)
  }

  const onGroupElements = () => {
    emit(SelectoComponentEvents.ON_SELECTO_GROUP, {
      targets: targets.value,
      selectedElements: selectedElements.value,
      groupData: groupSelectedExist.value,
    })
  }

  const onUnGroupElements = () => {
    targets.value = []
    emit(SelectoComponentEvents.ON_SELECTO_UNGROUP, {
      targets: targets.value,
      selectedElements: selectedElements.value,
      groupData: groupSelectedExist.value,
    })
  }

  const onGroupClone = () => {
    emit(SelectoComponentEvents.ON_SELECTO_CLONE, {
      targets: targets.value,
      selectedElements: selectedElements.value,
      groupData: groupSelectedExist.value,
    })
  }

  const onGroupDelete = () => {
    emit(SelectoComponentEvents.ON_SELECTO_DELETE, {
      targets: targets.value,
      selectedElements: selectedElements.value,
      groupData: groupSelectedExist.value,
    })
  }

  const makeActiveGroupWithSelectedElements = (group: GroupFields<GroupFieldsOptions>, selectedElementIds: string[] = []) => {
    if (group && group.id) {
      if (group && group.children && group.children.length > 0) {
        const cgChild = group.children.map(({ elementId }) => elementId)
        const elementIds = [...new Set([...selectedElementIds, ...cgChild])]
        const selectedElements = elementIds.map(elementId => {
          const selector = '.select-o-field[data-element-id="' + elementId + '"]'
          return document.querySelector(selector)
        })
        targets.value = selectedElements
      }
    }
  }

  const makeActiveGroup = (group: GroupFields<GroupFieldsOptions>) => {
    if (group && group.id) {
      targets.value = []
      if (group && group.children && group.children.length > 0) {
        const selectedElements = group.children.map((value: { pageId: string; elementId: string }) => {
          const selector = '.select-o-field[data-element-id="' + value.elementId + '"]'

          return document.querySelector(selector)
        })
        targets.value = selectedElements
      }
      activeGroupInfo.value = group
    }
  }

  watch(
    () => props.activeGroup,
    updatedGroup => {
      if (updatedGroup) {
        setTimeout(() => makeActiveGroup(updatedGroup), 50)
      } else {
        targets.value = []
        activeGroupInfo.value = null
        groupTooltipAbleRef.value = null
        cloneAndAddElementToGroup.value = null
        moveableSelectionRef.value = null
        groupRequiredAbleRef.value = null
        selectoRef.value = null
        moveableSelectionRef.value?.forceUpdate()
        selectoRef.value?.forceUpdate()
      }
    },
    { immediate: true, deep: true }
  )

  const cloneAndAddToGroup = (event: any) => {
    event.stopPropagation()
    emit(SelectoComponentEvents.ON_CLONE_AND_ADD_TO_GROUP, activeGroupInfo.value?.id)
  }

  const requiredGroup = computed(() => {
    return groupExist.value && groupSelectedExist.value && getRequiredValidation(groupSelectedExist.value)
  })

  const elementGuidelines = computed(() => ['.el-wrapper', 'section'])
</script>

<template>
  <div
    v-if="targets.length > 0"
    ref="groupTooltipAbleRef"
    :style="{
      display: isEditable && displayToolTip && !isDragging ? 'flex' : 'none',
      zIndex: 101,
      position: 'absolute',
      willChange: 'position',
    }"
    class="moveable-wrapper--ables actions absolute -mt-8 flex"
  >
    <CommonGroupTooltip
      :groupExist="groupExist"
      @on-group-elements="onGroupElements"
      @on-ungroup-elements="onUnGroupElements"
      @on-group-delete="onGroupDelete"
      @on-group-clone="onGroupClone"
    />
  </div>
  <div
    v-if="targets.length > 0"
    ref="cloneAndAddElementToGroup"
    :style="{
      display: isEditable && displayAddToGroupToolTip && !isDragging ? 'flex' : 'none',
      zIndex: 101,
      position: 'absolute',
      willChange: 'position',
    }"
    class="moveable-wrapper--ables flex"
  >
    <UITooltip :placement="'bottom'">
      <template #trigger>
        <div class="text-primary-700 cursor-pointer flex" @click="cloneAndAddToGroup">
          <PlusSquareIcon class="w-6 h-6 p-1" />
        </div>
      </template>
      {{ $t('common.addCheckboxToGroup') }}
    </UITooltip>
  </div>
  <span
    ref="groupRequiredAbleRef"
    :style="{
      display: requiredGroup ? 'flex' : 'none',
      zIndex: 101,
      position: 'absolute',
      willChange: 'position',
    }"
    class="option--required"
  >
    *
  </span>
  <Moveable
    :key="`moveable-component-with-selecto`"
    :throttleDrag="1"
    :useResizeObserve="true"
    :useMutationObserver="true"
    :useAccuratePosition="true"
    ref="moveableSelectionRef"
    :draggable="isDragEnabled"
    :target="targets"
    :origin="false"
    @dragGroup="onDragGroup"
    @dragGroupEnd="onDragGroupEnd"
    @dragGroupStart="onDragGroupStart"
    :snappable="true"
    :snapContainer="snapContainer"
    :elementGuidelines="elementGuidelines"
    :bounds="bounds"
    :hideChildMoveableDefaultLines="false"
    :isDisplaySnapDigit="false"
    :isDisplayInnerSnapDigit="false"
    :edgeDraggable="false"
    :snapGap="true"
    :snapDirections="directions"
    :snapThreshold="props.snapThreshold"
    :elementSnapDirections="elementSnapDirections"
    @changeTargets="onChangeTargets"
  ></Moveable>

  <Selecto
    ref="selectoRef"
    :dragContainer="'.pages-wrapper'"
    :selectableTargets="['.select-o-field']"
    :hitRate="0"
    :selectByClick="true"
    :selectFromInside="true"
    :toggleContinueSelect="['shift']"
    :ratio="0"
    @dragStart="onDragStart"
    @selectEnd="onSelectEnd"
  ></Selecto>
</template>

<style lang="scss">
  .moveable-control-box {
    z-index: 100 !important;
  }
</style>
